import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import { listingCategoriesService } from "../../services/listing-categories.service";
import {
  ListingCategory,
  CreateListingCategoryDto,
} from "../../interfaces/listing-category.interface";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { useConfirm } from '../../hooks/useConfirm';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateListingCategoryDto = {
  key: "",
  isActive: true,
};

function getErrorMsg(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const r = (err as any).response?.data;
    if (r?.message) return Array.isArray(r.message) ? r.message.join(", ") : r.message;
  }
  return fallback;
}

// ─── Form ─────────────────────────────────────────────────────────────────────

interface CategoryFormProps {
  form: CreateListingCategoryDto;
  setF: (patch: Partial<CreateListingCategoryDto>) => void;
  saving: boolean;
  error: string;
  onSubmit: () => void;
  onCancel: () => void;
  isEdit: boolean;
}

const CategoryForm = ({ form, setF, saving, error, onSubmit, onCancel, isEdit }: CategoryFormProps) => {
  const { t } = useTranslation("common");

  const inputClass =
    "w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Key */}
      <div>
        <label className={labelClass}>{t("key")} *</label>
        <input
          className={inputClass}
          placeholder="buy"
          value={form.key}
          onChange={(e) => setF({ key: e.target.value })}
          disabled={isEdit}
        />
        {isEdit && (
          <p className="mt-1 text-xs text-gray-400">{t("key_immutable_hint")}</p>
        )}
      </div>

      {/* Active */}
      <div className="flex items-center">
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            className="rounded"
            checked={form.isActive}
            onChange={(e) => setF({ isActive: e.target.checked })}
          />
          {t("active")}
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button variant="primary" size="sm" onClick={onSubmit} disabled={saving}>
          {saving ? t("loading") : isEdit ? t("save_changes") : t("create")}
        </Button>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ListingCategoriesPage = () => {
  const { t } = useTranslation("common");
  const { confirm, ConfirmDialog } = useConfirm();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.SYSTEM_SETTINGS_UPDATE);

  const [categories, setCategories] = useState<ListingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ListingCategory | null>(null);
  const [form, setForm] = useState<CreateListingCategoryDto>(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<ListingCategory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listingCategoriesService.list();
      setCategories(data);
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_load")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setDialogOpen(true);
  };

  const openEdit = (cat: ListingCategory) => {
    setEditTarget(cat);
    setForm({ key: cat.key, isActive: cat.isActive });
    setFormError("");
    setDialogOpen(true);
  };

  const setF = (patch: Partial<CreateListingCategoryDto>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async () => {
    if (!form.key.trim()) { setFormError(t("error_key_required")); return; }

    if (!await confirm(t('confirm_save_listing_category'))) return;
    setSaving(true);
    setFormError("");
    try {
      if (editTarget) {
        const updated = await listingCategoriesService.update(editTarget.id, form);
        setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      } else {
        const created = await listingCategoriesService.create(form);
        setCategories((prev) => [...prev, created]);
      }
      setDialogOpen(false);
    } catch (e) {
      setFormError(getErrorMsg(e, t("failed_to_save")));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await listingCategoriesService.remove(deleteTarget.id);
      setCategories((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_delete")));
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (cat: ListingCategory) => {
    if (!await confirm(t('confirm_toggle_category_active'))) return;
    try {
      const updated = await listingCategoriesService.update(cat.id, { isActive: !cat.isActive });
      setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_save")));
    }
  };

  return (
    <Layout title={t("listing_categories")}>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("listing_categories_desc")}
          </p>
          {canEdit && (
            <Button variant="primary" size="sm" onClick={openCreate}>
              + {t("create")}
            </Button>
          )}
        </div>

        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-400">{t("loading")}</div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12 text-gray-400">{t("noData")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Card
                key={cat.id}
                className={`p-4 space-y-3 ${!cat.isActive ? "opacity-50" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {cat.key}
                  </span>
                  {!cat.isActive && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 font-medium">
                      {t("inactive")}
                    </span>
                  )}
                </div>

                {canEdit && (
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <Button variant="outline" size="sm" onClick={() => openEdit(cat)}>
                      {t("edit")}
                    </Button>
                    <Button
                      variant={cat.isActive ? "secondary" : "primary"}
                      size="sm"
                      onClick={() => toggleActive(cat)}
                    >
                      {cat.isActive ? t("deactivate") : t("activate")}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      className="ml-auto"
                      onClick={() => setDeleteTarget(cat)}
                    >
                      {t("delete")}
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog
        open={dialogOpen}
        title={editTarget ? t("edit_listing_category") : t("create_listing_category")}
        onClose={() => setDialogOpen(false)}
      >
        <CategoryForm
          form={form}
          setF={setF}
          saving={saving}
          error={formError}
          onSubmit={handleSubmit}
          onCancel={() => setDialogOpen(false)}
          isEdit={!!editTarget}
        />
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!deleteTarget}
        title={t("delete_listing_category")}
        onClose={() => setDeleteTarget(null)}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t("delete_listing_category_confirm")}{" "}
            <strong>{deleteTarget?.key}</strong>?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("cancel")}
            </Button>
            <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleting}>
              {deleting ? t("loading") : t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
      {ConfirmDialog}
    </Layout>
  );
};

export default ListingCategoriesPage;
